import { Footer } from 'components/ui/AppShellFallback';
import { Box } from '@mui/material';
import React, { useMemo } from 'react';

import { ViewTrackerProvider } from '../../providers/ViewTrackerProvider';
import { useUser, TrialState } from '../../providers/UserProvider';
import { Container } from '../../ui/Container';
import { config } from '../../../config';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { formatTimeRemaining } from '../admin/UserList';

import { AlertBox } from 'components/ui/Feedback';
import NotificationMessage from '../../ui/NotificationMessage';
interface ClientContainerProps {
    children: React.ReactNode;
    withHeader?: boolean;
}

const InsightsFooter = () => {
    return <Footer sx={{ mt: 0, '@media print': { display: 'none' } }} />;
};

const ClientContainer = ({
    children,
    withHeader = true,
}: ClientContainerProps) => {
    const { trialState } = useUser();
    const { data: userData } = useQuery(
        gql`
            query me {
                me {
                    trialMinLeft
                }
            }
        `,
        { fetchPolicy: 'network-only' }
    );
    const daysRemaining = (remainDays: string) =>
        `you have ${remainDays} free access to select articles`;
    const expiryMessage = useMemo(() => {
        return userData?.me.trialMinLeft
            ? userData.me.trialMinLeft < 24 * 60 // 1 day
                ? 'your trial expires today'
                : daysRemaining(
                      formatTimeRemaining(userData.me.trialMinLeft, true)
                  )
            : daysRemaining(`${config.trialDays} days`);
    }, [userData]);

    return (
        <ViewTrackerProvider>
            <NotificationMessage />
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 0 auto',
                    flexDirection: 'column',
                    minHeight: withHeader ? 0 : '100vh',
                    minWidth: 0,
                    '&>div>div': { minWidth: 0 },
                    '@supports (min-height: 100dvh)': {
                        minHeight: withHeader ? 0 : '100dvh',
                    },
                }}
            >
                {trialState === TrialState.TrialContinues && (
                    <Box>
                        <AlertBox
                            id="trial-user-alert-box"
                            visible={true}
                            children={`Welcome to Neon Insights, ${expiryMessage}. A member of Marex will contact you to complete your registration.`}
                            styleName="info"
                        />
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 0 auto',
                        minHeight: 0,
                        '& > div > div:last-child': { minWidth: 0 },
                    }}
                >
                    <Container>
                        <Box
                            sx={{
                                flexGrow: 1,
                                mt: withHeader ? { xs: 2, lg: 4 } : 0,
                                mb: 7,
                                minWidth: 0,
                            }}
                        >
                            {children}
                        </Box>
                    </Container>
                    <InsightsFooter />
                </Box>
            </Box>
        </ViewTrackerProvider>
    );
};

export default ClientContainer;
